import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lock, Play, Clock } from "lucide-react";
import PaidUnlockButton from "@/components/PaidUnlockButton";

function isYouTube(url) {
  return /youtube\.com|youtu\.be/i.test(url || "");
}
function ytEmbed(url) {
  const m = url.match(/(?:v=|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1` : url;
}
function isVimeo(url) {
  return /vimeo\.com/i.test(url || "");
}
function vimeoEmbed(url) {
  // Vimeo URLs come in several formats:
  //   1. https://vimeo.com/123456789                    (public)
  //   2. https://vimeo.com/123456789/abc1234567         (unlisted with privacy hash)
  //   3. https://player.vimeo.com/video/123/?h=abc      (already-embed form)
  //   4. https://vimeo.com/123/abc?share=copy           (mobile share link)
  // The privacy hash (h= or the /HASH path segment) bypasses Vimeo's "where can
  // this be embedded?" domain allowlist — critical for getting unlisted videos
  // to play on a custom domain without changing Vimeo settings.
  if (!url) return url;
  // /video/{id} embed form — preserve any existing ?h=
  let m = url.match(/player\.vimeo\.com\/video\/(\d+)(?:\?(.*))?/i);
  if (m) {
    const id = m[1];
    const params = new URLSearchParams(m[2] || "");
    params.set("autoplay", "1");
    params.set("title", "0");
    params.set("byline", "0");
    params.set("portrait", "0");
    params.set("dnt", "1");
    return `https://player.vimeo.com/video/${id}?${params.toString()}`;
  }
  // vimeo.com/{id}[/{hash}][?share=...] form
  m = url.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/i);
  if (m) {
    const id = m[1];
    const hash = m[2];
    const params = new URLSearchParams();
    if (hash) params.set("h", hash);
    params.set("autoplay", "1");
    params.set("title", "0");
    params.set("byline", "0");
    params.set("portrait", "0");
    params.set("dnt", "1");
    return `https://player.vimeo.com/video/${id}?${params.toString()}`;
  }
  return url;
}
function VideoError({ item, onRetry }) {
  return (
    <div className="absolute inset-0 bg-black/85 grid place-items-center px-6">
      <div className="text-center max-w-md">
        <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-3">
          Playback issue
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-3">
          Video is having trouble loading
        </h2>
        <p className="text-sm text-gray-300 leading-relaxed">
          If this keeps happening, the video host (Vimeo/YouTube) may have
          embed restrictions for this site. Refresh below, or open the video
          directly.
        </p>
        <div className="mt-5 flex gap-3 justify-center flex-wrap">
          <button
            onClick={onRetry}
            className="rounded-full bg-white text-black px-5 py-2.5 text-sm font-semibold hover:bg-gray-200"
          >
            Try again
          </button>
          {item?.video_url && (
            <a
              href={item.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-white/10 text-white px-5 py-2.5 text-sm font-semibold hover:bg-white/15 border border-white/20"
            >
              Open in new tab
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Watch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [item, setItem] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  // Refetch content whenever the user's subscription state changes.
  // KEY FIX: if a user just paid (auto-recover layer flips is_subscribed=true
  // milliseconds after this page mounts), this refetch picks up the unlocked
  // video_url without requiring the user to refresh or click again.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api.get(`/content/${id}`)
      .then((r) => { if (!cancelled) setItem(r.data); })
      .catch(() => navigate("/browse"));
    return () => { cancelled = true; };
  }, [id, user?.is_subscribed, navigate]);

  // Additional safety net: if the page is showing the locked screen but the
  // user IS subscribed (race condition between API call and auth state), retry
  // once after a short delay.
  useEffect(() => {
    if (!item || !user) return;
    if (user.is_subscribed && item.locked) {
      const t = setTimeout(() => {
        api.get(`/content/${id}`).then((r) => setItem(r.data)).catch(() => {});
      }, 800);
      return () => clearTimeout(t);
    }
  }, [item, user, id]);

  if (!item || !user) {
    return <div className="min-h-screen bg-[#0A0A0E] grid place-items-center text-gray-400">Loading…</div>;
  }

  const isLocked = item.locked;
  const isComing = !!item.coming_soon;
  const goSubscribe = () => navigate("/subscribe");

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white">
      <Header />
      <div className="pt-20">
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 pt-6">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="mb-4 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black border border-white/10">
            {isComing ? (
              <>
                <img
                  src={item.backdrop_url || item.poster_url}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-black/65 backdrop-blur-sm grid place-items-center">
                  <div data-testid="coming-soon-overlay" className="text-center max-w-md px-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/40 mb-5">
                      <Clock className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-3">Coming Soon</div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
                      {item.title}
                    </h2>
                    <p className="mt-3 text-gray-300">
                      Not yet available. Subscribe now and we'll unlock it for you the moment it drops on YBTV.
                    </p>
                    {!user.is_subscribed && (
                      <Button
                        data-testid="coming-subscribe-btn"
                        onClick={goSubscribe}
                        className="mt-6 rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-7 py-6 text-base font-semibold"
                      >
                        Subscribe — $3.99/mo
                      </Button>
                    )}
                  </div>
                </div>
              </>
            ) : !isLocked && item.video_url ? (
              isYouTube(item.video_url) ? (
                <iframe
                  data-testid="video-player"
                  src={ytEmbed(item.video_url)}
                  title={item.title}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              ) : isVimeo(item.video_url) ? (
                <iframe
                  data-testid="video-player"
                  src={vimeoEmbed(item.video_url)}
                  title={item.title}
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                  allowFullScreen
                />
              ) : (
                <video
                  data-testid="video-player"
                  src={item.video_url.startsWith("/api/media/") ? `${process.env.REACT_APP_BACKEND_URL}${item.video_url}` : item.video_url}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-full bg-black"
                />
              )
            ) : (
              <>
                <img
                  src={item.backdrop_url || item.poster_url}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-50"
                />
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm grid place-items-center">
                  <div data-testid="locked-overlay" className="text-center max-w-md px-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/40 mb-5">
                      <Lock className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
                      Subscribe to watch
                    </h2>
                    <p className="mt-2 text-gray-300">
                      Get unlimited access to <span className="text-white font-semibold">{item.title}</span> and the entire YBTV library for $3.99/month.
                    </p>
                    <Button
                      data-testid="locked-subscribe-btn"
                      onClick={goSubscribe}
                      className="mt-6 rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-7 py-6 text-base font-semibold"
                    >
                      <Play className="w-4 h-4 mr-2 fill-white" /> Unlock for $3.99/mo
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-10">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-2">
                {item.type}
              </div>
              <h1 data-testid="watch-title" className="font-display text-3xl md:text-5xl font-black tracking-tighter">{item.title}</h1>
              <div className="mt-3 flex items-center gap-3 text-sm text-gray-300 flex-wrap">
                {item.year && <span>{item.year}</span>}
                {item.genre && <span className="px-2 py-0.5 border border-white/15 rounded-full text-xs uppercase tracking-wider">{item.genre}</span>}
                {item.duration && <span>{item.duration}</span>}
              </div>
            </div>
          </div>
          <p className="mt-6 text-gray-300 max-w-3xl leading-relaxed">{item.description}</p>
        </div>
      </div>
    </div>
  );
}
